import React from 'react';
import Icon from '../../../components/AppIcon';

const TransferHistory = ({ transferHistory }) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount, currency = '€') => {
    if (!amount || amount === 0) return 'Free Transfer';
    if (amount >= 1000000) {
      return `${currency}${(amount / 1000000)?.toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `${currency}${(amount / 1000)?.toFixed(0)}K`;
    }
    return `${currency}${amount}`;
  };

  const getTransferTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'permanent':
        return 'ArrowRightLeft';
      case 'loan':
        return 'RefreshCw';
      case 'free':
        return 'Gift';
      default:
        return 'TrendingUp';
    }
  };

  const getTransferTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'permanent':
        return 'bg-primary/10 text-primary';
      case 'loan':
        return 'bg-accent/10 text-accent-foreground';
      case 'free':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (!transferHistory || transferHistory?.length === 0) {
    return (
      <section className="py-12">
        <h2 className="text-3xl font-bold text-foreground font-headline mb-8">Transfer History</h2>
        <div className="bg-muted/50 border border-border rounded-lg p-12 text-center">
          <Icon name="ArrowRightLeft" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No transfer history available yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-foreground font-headline">Transfer History</h2>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon name="History" size={20} />
          <span className="text-sm">{transferHistory?.length} transfers</span>
        </div>
      </div>
      
      <div className="space-y-6">
        {transferHistory?.map((transfer, index) => (
          <div
            key={transfer?.id || index}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div className={`px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-2 ${getTransferTypeColor(transfer?.transferType)}`}>
                  <Icon name={getTransferTypeIcon(transfer?.transferType)} size={16} />
                  {transfer?.transferType || 'Transfer'}
                </div>
                <p className="text-sm text-muted-foreground">{formatDate(transfer?.transferDate)}</p>
              </div>
              
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(transfer?.transferFee, transfer?.currency)}
                </p>
                {transfer?.transferFee > 0 && (
                  <p className="text-xs text-muted-foreground">Transfer Fee</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="flex-1 text-right">
                <p className="text-lg font-bold text-foreground mb-1">{transfer?.fromClub || 'Unknown Club'}</p>
                <p className="text-sm text-muted-foreground">{transfer?.fromLeague || 'N/A'}</p>
              </div>
              
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                <Icon name="ArrowRight" size={24} className="text-primary" />
              </div>
              
              <div className="flex-1 text-left">
                <p className="text-lg font-bold text-foreground mb-1">{transfer?.toClub || 'Unknown Club'}</p>
                <p className="text-sm text-muted-foreground">{transfer?.toLeague || 'N/A'}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-border">
              {transfer?.contractLength && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Contract Length</p>
                  <p className="text-sm font-semibold text-foreground">{transfer?.contractLength}</p>
                </div>
              )}
              {transfer?.marketValueAtTransfer && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Market Value</p>
                  <p className="text-sm font-semibold text-foreground">
                    {formatCurrency(transfer?.marketValueAtTransfer, transfer?.currency)}
                  </p>
                </div>
              )}
              {transfer?.season && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Season</p>
                  <p className="text-sm font-semibold text-foreground">{transfer?.season}</p>
                </div>
              )}
            </div>
            
            {transfer?.notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-1">Transfer Details</p>
                <p className="text-foreground text-sm">{transfer?.notes}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default TransferHistory;